import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

/**
 * LikeKind FAQ — Aligned with V1 (Activity-First Coordination)
 * 
 * Structure:
 * 1. CORE FAQ (What is LikeKind, Activities, Map, Signals)
 * 2. FAMILY & CHILD SAFETY FAQ
 * 3. MEMBERSHIP FAQ
 * 4. SPONSORSHIP FAQ
 * 5. PRIVACY FAQ
 */

const FAQ_SECTIONS = [
  {
    id: 'core',
    title: 'CORE FAQ',
    questions: [
      {
        id: 'what-is-likekind',
        question: 'What is LikeKind?',
        answer: `LikeKind helps people discover, create, and join real-world activities near them.

It's built around coordination — organizing things and showing up — rather than content, feeds, or social performance.

The goal is simple: help people see opportunities, show up, and keep things going.`,
        tags: ['about', 'platform', 'coordination']
      },
      {
        id: 'what-is-activity',
        question: 'What is an activity?',
        answer: `Activities are the core of LikeKind.

They represent real-world plans that people can join, coordinate, and participate in.

Activities appear on the map and in Base Camp. They move through simple states based on participation:
• Needs people — not enough participants yet
• Good to go — enough people are going

Activities are shown based on time and location, never ranked by popularity.`,
        tags: ['activities', 'coordination', 'participation']
      },
      {
        id: 'how-start-something',
        question: 'How do I start something?',
        answer: `You can create an activity directly from the map in seconds.

No setup or profile building required.

Tap the button on the map, pick a template or create your own, set the time, and publish. That's it.`,
        tags: ['create', 'activities', 'map']
      },
      {
        id: 'how-coordinate-without-messaging',
        question: 'How do people coordinate without messaging?',
        answer: `LikeKind uses simple signals instead of conversation threads.

Participation signals include:
• Going
• Interested
• Can Help
• Bringing Equipment
• Need Ride / Can Drive

For community activities, additional signals exist:
• Need Volunteers
• Need Tools
• Need Supplies

These signals give organizers what they need to know without requiring open-ended conversations.`,
        tags: ['signals', 'coordination', 'participation']
      },
      {
        id: 'what-is-base-camp',
        question: 'What is Base Camp?',
        answer: `Base Camp is a simple, chronological view of activity.

It is not personalized, ranked, or algorithm-driven.

It shows activities you're connected to in the order they were created or are upcoming. It's meant to feel calm, clear, and manageable.`,
        tags: ['base camp', 'chronological']
      },
      {
        id: 'what-is-map',
        question: 'What is the Map?',
        answer: `The Map is the primary way to discover what's happening nearby.

Activities appear as pins on the map. You can filter by category tags (Sports, Outdoors, Community, etc.) and by time (Tonight, Today, This week).

Anchor locations like parks, courts, and venues appear as reference points. Tapping one shows what's happening at that place.`,
        tags: ['map', 'discover', 'activities']
      },
      {
        id: 'how-different-social-media',
        question: 'How is LikeKind different from social media?',
        answer: `LikeKind is built for coordination — helping people organize real things and show up.

Activities are shown based on time and location. Nothing is ranked by engagement or popularity. The structure is activity-first — everything centers on real-world participation.`,
        tags: ['social media', 'difference', 'coordination']
      },
      {
        id: 'does-use-algorithms',
        question: 'Does LikeKind use algorithms?',
        answer: `Activities are shown based on time and location.

Nothing is ranked, boosted, or amplified. This is a core part of how LikeKind works.`,
        tags: ['algorithm', 'ranking', 'chronological']
      },
      {
        id: 'is-social-media',
        question: 'Is LikeKind social media?',
        answer: `LikeKind is a coordination system for real-world activity.

Participation happens through signals — not feeds, followers, or content. It avoids social media patterns by design.`,
        tags: ['social media', 'coordination']
      },
      {
        id: 'what-are-broadcasts',
        question: 'What are broadcast updates?',
        answer: `Broadcast updates are simple status signals posted by the activity host.

Types:
• On — the activity is happening as planned
• Delayed — the activity is delayed
• Location change — the location has changed
• Canceled — the activity is canceled

These replace the need for group chats or message threads.`,
        tags: ['broadcasts', 'coordination', 'activities']
      },
      {
        id: 'messaging',
        question: 'Can I message people?',
        answer: `Communication is structured around activities — most coordination happens through participation signals like "Going," "Interested," or "Can Drive."

Direct messaging exists only where necessary for coordination.`,
        tags: ['messaging', 'coordination']
      },
      {
        id: 'who-sees-activity',
        question: 'Who can see my activity?',
        answer: `Public activities are visible to anyone on the map and in Base Camp.

Your personal participation (which activities you've joined) is visible according to your privacy settings.

LikeKind does not make profiles broadly discoverable by default.

Children's accounts have additional protections and are never discoverable by strangers.`,
        tags: ['privacy', 'visibility']
      },
      {
        id: 'can-strangers-find-me',
        question: 'Can strangers find me on LikeKind?',
        answer: `LikeKind is structured around activities and participation. Profiles are not broadly discoverable.

Children are never discoverable by strangers, and youth accounts have additional safety protections.`,
        tags: ['privacy', 'strangers', 'safety']
      },
      {
        id: 'location-travel',
        question: 'How does location work?',
        answer: `Your location helps LikeKind show relevant nearby activities on the map.

LikeKind does not track precise location in the background. Location is used only when you're actively viewing the map.`,
        tags: ['location', 'map', 'privacy']
      },
      {
        id: 'stop-using',
        question: 'What happens if I stop using LikeKind?',
        answer: `Nothing happens.

You can leave and return whenever you want. LikeKind is designed so participation is always optional.`,
        tags: ['leaving', 'optional', 'participation']
      },
      {
        id: 'why-chronological',
        question: 'Why is everything chronological?',
        answer: `Chronological order is the simplest and most transparent way to show activity.

It removes incentives to compete for attention and keeps the focus on participation.

This reinforces the platform's core design philosophy.`,
        tags: ['chronological', 'transparency']
      },
      {
        id: 'why-likekind-exists',
        question: 'Why does LikeKind exist?',
        answer: `LikeKind was built to help people coordinate real-world activities in a calmer way.

It's designed to help people notice opportunities, organize things, and stay connected with their communities — through participation, not performance.`,
        tags: ['purpose', 'philosophy']
      }
    ]
  },
  {
    id: 'family',
    title: 'FAMILY & CHILD SAFETY FAQ',
    questions: [
      {
        id: 'families-children',
        question: 'How do families and children use LikeKind?',
        answer: `LikeKind supports family accounts where parents manage child and teen participation.

Children cannot create independent accounts.

Safety protections include:
• no discoverability for children
• parent visibility controls
• restrictions on messaging and interactions

LikeKind is designed to be a child's first account when created through a parent or school.`,
        tags: ['families', 'children', 'safety']
      },
      {
        id: 'safe-for-children',
        question: 'Is LikeKind safe for children?',
        answer: `LikeKind includes a structured family account system designed for safety.

Children cannot create independent accounts and must be managed by a parent or guardian.

Protections include:
• no discoverability for children
• parent visibility controls
• restrictions on messaging and interactions`,
        tags: ['children', 'safety', 'parents']
      },
      {
        id: 'parent-visibility',
        question: 'What is parent visibility?',
        answer: `Parent visibility allows parents to see what their children are doing on LikeKind.

Control modes include:
• ON — Full parent visibility (mandatory for children under 13)
• SHARED — Parent and child both see activity context
• OFF — Reduced parent visibility (only for youth adults where enabled)

Children are always informed when parent visibility is active.`,
        tags: ['parents', 'visibility', 'children', 'safety']
      },
      {
        id: 'child-accounts',
        question: 'Can children create their own accounts?',
        answer: `All child accounts are created and managed by a parent or guardian. Children cannot create independent accounts. This is a structural safety requirement.`,
        tags: ['children', 'accounts', 'safety']
      },
      {
        id: 'child-messaging',
        question: 'Can children message each other?',
        answer: `Child-to-child direct messaging is not available and cannot be enabled. This is a structural safety protection.`,
        tags: ['children', 'messaging', 'safety']
      },
      {
        id: 'teen-accounts',
        question: 'How do teen accounts work?',
        answer: `Teen accounts (ages 13-15) have limited discoverability.

Parent visibility defaults to ON and may be set to SHARED based on settings.

Teens cannot override parent visibility settings.

Child-to-child direct messaging remains disabled.`,
        tags: ['teens', 'accounts', 'safety']
      }
    ]
  },
  {
    id: 'membership',
    title: 'MEMBERSHIP FAQ',
    questions: [
      {
        id: 'memberships-exist',
        question: 'What memberships exist?',
        answer: `Open Member
• Default membership
• Full Base Camp access
• Join and create activities
• Map access
• Maximum privacy by default

Personal Member
• Everything in Open Member
• Create circles
• Expression space on profile
• Personal timeline`,
        tags: ['membership', 'tiers']
      },
      {
        id: 'why-not-free',
        question: 'Why isn\'t it called "free"?',
        answer: `Because there is no catch.

Privacy is not a premium feature.

It's the foundation.`,
        tags: ['free', 'privacy', 'membership']
      },
      {
        id: 'how-makes-money',
        question: 'How does LikeKind make money?',
        answer: `LikeKind may be supported by sponsorship placements.

These are not behavioral advertisements.

Sponsors do not receive user data and cannot target users based on activity or personal information.`,
        tags: ['money', 'revenue', 'sponsorship']
      }
    ]
  },
  {
    id: 'sponsorship',
    title: 'SPONSORSHIP FAQ',
    questions: [
      {
        id: 'sponsor-appear',
        question: 'Where do sponsors appear?',
        answer: `Sponsors appear as static, disclosed placements — not behavioral ads.

They do not appear in personal views or activity feeds.

Sponsors do not receive user data and cannot target users.`,
        tags: ['sponsors', 'placement', 'privacy']
      },
      {
        id: 'sponsor-rules',
        question: 'What rules apply to sponsors?',
        answer: `Sponsors must:
• upload materials through LikeKind
• accept placement standards
• sign off on platform rules and philosophy

Sponsors cannot influence what activities are shown or how they are ranked.`,
        tags: ['sponsors', 'rules']
      },
      {
        id: 'businesses-advertise',
        question: 'Can businesses advertise on LikeKind?',
        answer: `Businesses can participate through sponsorship placements, not traditional advertising.

Sponsors support the platform's operation but do not receive user data or behavioral targeting tools.

No feed boosting. No targeting.`,
        tags: ['business', 'advertising', 'sponsorship']
      }
    ]
  },
  {
    id: 'privacy',
    title: 'PRIVACY FAQ',
    questions: [
      {
        id: 'how-privacy-works',
        question: 'How does privacy work on LikeKind?',
        answer: `LikeKind collects only the information required to operate the platform.

We do not sell personal data and we do not use behavioral advertising.

There is no behavioral tracking, no algorithmic profiling, and no data sales.

For families, parents can maintain visibility into child accounts using built-in safety controls.`,
        tags: ['privacy', 'data', 'safety']
      },
      {
        id: 'data-tracked',
        question: 'Is my data tracked?',
        answer: `No behavioral tracking exists.

If something cannot be used to help you coordinate, it is not collected.`,
        tags: ['privacy', 'tracking', 'data']
      },
      {
        id: 'sell-data',
        question: 'Does LikeKind sell my data?',
        answer: `LikeKind collects only what's needed to run the platform. Personal data is not sold, and there is no behavioral advertising.`,
        tags: ['privacy', 'data']
      },
      {
        id: 'are-there-ads',
        question: 'Are there ads?',
        answer: `There are placards, not ads.

Placards:
• are static
• are disclosed
• do not rotate by performance
• do not influence what you see

Sponsors do not receive user data.`,
        tags: ['ads', 'advertising', 'privacy']
      },
      {
        id: 'content-ownership',
        question: 'Who owns my content?',
        answer: `You do.

LikeKind does not monetize user content.`,
        tags: ['content', 'ownership', 'privacy']
      }
    ]
  }
];

const POPULAR_TAGS = [
  'privacy', 'activities', 'coordination', 'safety', 'children',
  'map', 'signals', 'social media', 'chronological', 'participation'
];

export default function FAQ() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [expandedSections, setExpandedSections] = useState(['core']);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const filteredSections = useMemo(() => {
    if (!searchQuery && selectedTags.length === 0) return FAQ_SECTIONS;
    const query = searchQuery.toLowerCase();
    return FAQ_SECTIONS.map(section => ({
      ...section,
      questions: section.questions.filter(q => {
        const matchesSearch = !query ||
          q.question.toLowerCase().includes(query) ||
          q.answer.toLowerCase().includes(query) ||
          q.tags.some(tag => tag.toLowerCase().includes(query));
        const matchesTags = selectedTags.length === 0 ||
          selectedTags.some(tag => q.tags.includes(tag));
        return matchesSearch && matchesTags;
      })
    })).filter(section => section.questions.length > 0);
  }, [searchQuery, selectedTags]);

  const toggleSection = (id) => {
    setExpandedSections(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const toggleTag = (tag) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };
  const clearFilters = () => { setSearchQuery(''); setSelectedTags([]); };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 hover:opacity-70" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
            </button>
            <h1 className="text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>FAQ</h1>
            <div className="w-10" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: 'var(--fy-text-disabled)' }} />
            <Input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search questions..."
              className="pl-10 h-12 rounded-full text-base"
              style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}
              data-testid="faq-search"
            />
            {(searchQuery || selectedTags.length > 0) && (
              <button onClick={clearFilters} className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:opacity-70" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
                <X className="w-4 h-4" style={{ color: 'var(--fy-text-secondary)' }} />
              </button>
            )}
          </div>
        </div>

        {/* Tags */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {POPULAR_TAGS.map(tag => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                className="px-3 py-1.5 rounded-full text-sm transition-colors"
                style={{
                  backgroundColor: selectedTags.includes(tag) ? 'var(--lk-sage)' : 'var(--fy-surface-muted)',
                  color: selectedTags.includes(tag) ? 'white' : 'var(--fy-text-secondary)'
                }}
                data-testid={`tag-${tag.replace(/\s+/g, '-')}`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {filteredSections.length === 0 ? (
            <Card className="p-8 text-center" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
              <p style={{ color: 'var(--fy-text-secondary)' }}>No questions match your search.</p>
              <Button onClick={clearFilters} variant="outline" className="mt-4">Clear Filters</Button>
            </Card>
          ) : (
            filteredSections.map(section => (
              <Card key={section.id} style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-4 text-left"
                  data-testid={`section-${section.id}`}
                >
                  <h2 className="font-bold" style={{ color: 'var(--fy-text-primary)' }}>{section.title}</h2>
                  <div className="flex items-center gap-2">
                    <span className="text-sm" style={{ color: 'var(--fy-text-disabled)' }}>{section.questions.length} questions</span>
                    {expandedSections.includes(section.id)
                      ? <ChevronUp className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
                      : <ChevronDown className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />}
                  </div>
                </button>
                {expandedSections.includes(section.id) && (
                  <div style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
                    {section.questions.map((q, idx) => (
                      <button
                        key={q.id}
                        onClick={() => setSelectedQuestion(q)}
                        className="w-full p-4 text-left hover:opacity-90 transition-opacity"
                        style={{ borderTop: idx > 0 ? '1px solid var(--fy-border-subtle)' : 'none' }}
                        data-testid={`question-${q.id}`}
                      >
                        <p className="font-medium" style={{ color: 'var(--fy-text-primary)' }}>{q.question}</p>
                        <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--fy-text-secondary)' }}>{q.answer.split('\n')[0]}...</p>
                      </button>
                    ))}
                  </div>
                )}
              </Card>
            ))
          )}
        </div>

        {/* Final Note */}
        <Card className="mt-8 p-6 text-center" style={{ backgroundColor: 'var(--lk-sage-muted)', border: 'none' }}>
          <p style={{ color: 'var(--lk-sage-dark)' }}>
            LikeKind is designed to help people notice opportunities, show up, coordinate, and keep what matters going.
          </p>
          <p className="mt-2 font-medium" style={{ color: 'var(--lk-sage-dark)' }}>
            Nothing more. Nothing hidden.
          </p>
        </Card>

        {/* Related Policies */}
        <div className="mt-8 p-6 rounded-xl" style={{ backgroundColor: 'var(--fy-background-secondary)', border: '1px solid var(--fy-border-subtle)' }}>
          <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>Related Policies</h3>
          <div className="flex flex-wrap gap-3">
            {[
              { to: '/trust', label: 'Trust & Safety' },
              { to: '/content-guidelines', label: 'Content Guidelines' },
              { to: '/privacy', label: 'Privacy Policy' },
              { to: '/parent-visibility', label: 'Parent Visibility & Child Safety' },
            ].map(link => (
              <Link key={link.to} to={link.to} className="px-4 py-2 rounded-full text-sm transition-colors hover:opacity-80"
                style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-primary)', border: '1px solid var(--fy-border-subtle)' }}>
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </main>

      {/* Question Modal */}
      {selectedQuestion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedQuestion(null)}>
          <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl" style={{ backgroundColor: 'var(--fy-background-primary)' }} onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between p-4" style={{ backgroundColor: 'var(--fy-surface-card)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
              <h3 className="font-bold text-lg pr-8" style={{ color: 'var(--fy-text-primary)' }}>{selectedQuestion.question}</h3>
              <button onClick={() => setSelectedQuestion(null)} className="absolute right-4 top-4 p-2 rounded-full hover:opacity-70" style={{ backgroundColor: 'var(--fy-surface-muted)' }} data-testid="close-modal">
                <X className="w-5 h-5" style={{ color: 'var(--fy-text-secondary)' }} />
              </button>
            </div>
            <div className="p-6">
              <div className="whitespace-pre-line text-sm leading-relaxed" style={{ color: 'var(--fy-text-primary)' }}>{selectedQuestion.answer}</div>
              <div className="flex gap-2 mt-6 flex-wrap">
                {selectedQuestion.tags.map(tag => (
                  <span key={tag} className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>{tag}</span>
                ))}
              </div>
            </div>
            <div className="p-4" style={{ borderTop: '1px solid var(--fy-border-subtle)' }}>
              <Button onClick={() => setSelectedQuestion(null)} className="w-full rounded-full" style={{ backgroundColor: 'var(--lk-sage)', color: 'white' }}>Got it</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
